<template>
  <div v-if="isCustomFieldTypeValid">
    <!-- Table cell display mode -->
    <template v-if="props.isTableCell">
      <div class="tw-flex tw-min-h-full tw-w-full tw-items-center">
        <div class="tw-flex tw-w-full tw-items-center tw-justify-between">
          <tooltip-text
            :text="fileDisplayText"
            max-width="calc(100% - 2rem)"
            class="tw-min-w-0 tw-flex-1"
          >
            <span class="tw-text-sm tw-text-gray-dark">
              {{ fileDisplayText }}
            </span>
          </tooltip-text>
          <p-btn
            v-if="localFiles.length"
            size="sm"
            flat
            class="tw-ml-auto tw-shrink-0 tw-rounded hover:tw-bg-gray-light"
            style="height: 24px"
            title="View file"
            @click.stop="viewFile"
          >
            <octicon
              icon="link-external"
              :width="16"
              :height="16"
              class="tw-text-blue"
            />
          </p-btn>
        </div>
      </div>
    </template>

    <!-- Regular form/modal display mode -->
    <template v-else>
      <label
        v-if="!props.useTrigger && !props.hideLabel"
        class="input-label tw-flex tw-items-center"
        :for="`${customField?.name}`"
      >
        {{ customField?.name }}
        <span
          v-if="props.customField.required"
          class="tw-ml-0.5 tw-text-sm tw-text-red"
        >
          *
        </span>
        <help-text v-if="!!customField.description" class="tw-ml-1">
          {{ customField.description }}
        </help-text>
        <span
          v-if="isMultiSelect && localFiles.length && !props.readonly"
          class="tw-mb-0.5 tw-ml-auto tw-inline-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-normal-case tw-text-primary hover:tw-opacity-70"
          @click="triggerFileInput"
        >
          <octicon
            icon="plus-circle"
            :height="14"
            :width="14"
            class="tw-mr-1"
          />
          Add
        </span>
      </label>
      <div
        :for="`${customField?.name}-file-input`"
        class="drag-area"
        tabindex="0"
        :class="{
          'drag-area--clickable': inputEmpty,
          'drag-area--dragging': dragActive && allowDrop,
          'drag-area--readonly': props.readonly,
          'tw-mt-0.5': !props.useTrigger,
        }"
        v-bind="{
          ...(props.useTrigger ? $attrs : {}),
        }"
        @click="onFileInputClick"
        @dragenter.prevent="setDragActive(true)"
        @dragleave.prevent="setDragActive(false)"
        @dragover.prevent="setDragActive(true)"
        @drop.prevent="addDraggedFiles"
        @keydown.enter.prevent="triggerFileInput"
      >
        <p-inner-loader v-if="loading" visible size="30px" />
        <div v-if="localFiles.length">
          <div
            v-for="(file, index) in localFiles"
            :key="file.fileName"
            class="tw-flex tw-items-center"
            :class="{
              'tw-mb-2': index !== localFiles.length - 1,
            }"
          >
            <tooltip-text
              :text="file.fileName"
              max-width="100%"
              class="tw-flex-1 tw-leading-0"
            >
              <a
                class="primary-link tw-truncate tw-text-xs"
                :href="file.fileUrl"
                target="_blank"
                >{{ file.fileName }}</a
              >
            </tooltip-text>
            <octicon
              v-if="
                !(props.customField?.required && localFiles.length === 1) &&
                !props.readonly
              "
              icon="x"
              :height="14"
              :width="14"
              class="tw-ml-auto tw-cursor-pointer tw-text-red hover:tw-opacity-70"
              @click.stop="removeFile(file)"
            />
          </div>
        </div>

        <span
          v-else-if="!!errorMessage && showErrorMessage && props.useTrigger"
          class="tw-text-red"
        >
          {{ errorMessage }}
        </span>

        <span v-else-if="!props.readonly" class="drag-area__placeholder">
          Click or drag to upload
        </span>

        <input
          :id="`${customField?.name}-file-input`"
          ref="fileInput"
          :style="{ display: 'none' }"
          :multiple="isMultiSelect"
          type="file"
          :accept="acceptedFileTypes"
          @change="onFilesSelected"
        />
      </div>
      <span
        v-if="!!errorMessage && showErrorMessage && !props.useTrigger"
        class="form-input__error-message"
      >
        {{ errorMessage }}
      </span>

      <asset-overwrite-confirm-notification
        ref="overwriteConfirmDialogRef"
        @confirm="handleOverwriteConfirm"
        @cancel="handleOverwriteCancel"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useRoot } from '@/utils/component';
import { computed, ref, watch } from 'vue';
import HelpText from '../design-system/misc/HelpText.vue';
import TooltipText from '../core/TooltipText.vue';
import AssetOverwriteConfirmNotification from './AssetOverwriteConfirmNotification.vue';
import { CustomField, CustomFieldType, AssetCustomFieldValue } from '@/api';
import Octicon from '@/components/core/icons/Octicon.vue';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: AssetCustomFieldValue[];
  useTrigger?: boolean;
  hideLabel?: boolean;
  assetUploadFn?: (file: File) => Promise<string>;
  readonly?: boolean;
  isTableCell?: boolean;
}
interface Emits {
  (e: 'value-updated', value: AssetCustomFieldValue[] | null): void;
  (e: 'open-modal'): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const root = useRoot();

const fileInput = ref<HTMLInputElement | null>(null);
const dragActive = ref(false);
const loading = ref(false);
const overwriteConfirmDialogRef =
  ref<InstanceType<typeof AssetOverwriteConfirmNotification>>();
const pendingFiles = ref<FileList | null>(null);

const localFiles = ref<AssetCustomFieldValue[]>([]);

const setDragActive = (active: boolean) => {
  if (!allowDrop.value) return;
  dragActive.value = active;
};

watch(
  () => props.value,
  (newValue) => {
    if (!newValue) {
      localFiles.value = [];
      return;
    }
    localFiles.value = newValue as AssetCustomFieldValue[];
  },
  { immediate: true, deep: true }
);

function triggerFileInput() {
  if (!allowDrop.value) return;
  fileInput.value?.click();
}

function onFileInputClick() {
  if (!allowDrop.value) return;
  if (inputEmpty.value) {
    triggerFileInput();
  }
}

const isCustomFieldTypeValid = computed(() => {
  return props.customField.type === CustomFieldType.ASSET;
});

const isMultiSelect = computed(() => {
  return props.customField.isMultiSelect;
});

const inputEmpty = computed(() => {
  return !localFiles.value.length;
});

const acceptedFileTypes = computed(() => {
  const mimeTypes = props.customField.mimeTypes;
  if (!mimeTypes?.length) {
    return undefined;
  }
  return mimeTypes.join(',');
});

async function addDraggedFiles(event: DragEvent) {
  dragActive.value = false;
  if (!allowDrop.value) return;
  const files = event.dataTransfer?.files;
  if (!files?.length) {
    return;
  }
  addFiles(files);
}

function onFilesSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (!files?.length) {
    return;
  }
  addFiles(files);
}

async function setLocalAndEmit(files: FileList, overwrite = false) {
  // Upload all the files to the server and get the URLs
  const uploadFn = props.assetUploadFn || root.$store.organization.uploadFile;
  const promises: Array<Promise<string>> = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    promises.push(uploadFn(file));
  }
  loading.value = true;
  const urls = await Promise.all(promises);

  localFiles.value = [
    ...(overwrite ? [] : localFiles.value),
    ...Array.from(files).map((file, index) => ({
      fileName: file.name,
      fileUrl: urls[index],
    })),
  ];
  emit('value-updated', localFiles.value);
}

async function addFiles(files: FileList) {
  // validate MIME types
  if (props.customField.mimeTypes?.length) {
    const invalidFiles = Array.from(files).filter(
      (file) => !props.customField.mimeTypes?.includes(file.type)
    );
    if (invalidFiles.length) {
      root.$notify({
        type: 'error',
        message: `Invalid file type. Acceptable file types are: ${
          props.customField.fileTypes?.join(', ') ||
          props.customField.mimeTypes?.join(', ')
        }`,
      });
      return;
    }
  }
  if (!isMultiSelect.value && files.length > 1) {
    root.$notify({
      type: 'error',
      message: 'Only one file is allowed',
    });
    return;
  }

  if (!isMultiSelect.value && localFiles.value.length) {
    pendingFiles.value = files;
    overwriteConfirmDialogRef.value?.showDialog();
    return;
  }

  await setLocalAndEmit(files);
  loading.value = false;
}

async function handleOverwriteConfirm() {
  if (pendingFiles.value) {
    loading.value = true;
    await setLocalAndEmit(pendingFiles.value, true);
    loading.value = false;
    pendingFiles.value = null;
  }
}

function handleOverwriteCancel() {
  pendingFiles.value = null;
}

function removeFile(file: AssetCustomFieldValue) {
  localFiles.value = localFiles.value.filter((f) => f !== file);
  // Emit null when empty to signal "clear field" - backend expects null, not empty array
  emit('value-updated', localFiles.value.length ? localFiles.value : null);
}

const errorMessage = computed(() => {
  if (props.customField.required && !localFiles.value.length) {
    return 'This field is required';
  }
  return '';
});

const showErrorMessage = ref(false);
watch(
  localFiles,
  () => {
    showErrorMessage.value = false;
  },
  { deep: true }
);

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

const allowDrop = computed(() => {
  return !props.readonly;
});

watch(isMultiSelect, (newValue) => {
  if (!newValue) {
    localFiles.value = [];
  }
});

const fileDisplayText = computed(() => {
  if (!localFiles.value.length) {
    return '—';
  }
  if (localFiles.value.length === 1) {
    return localFiles.value[0].fileName;
  }
  return `${localFiles.value.length} files`;
});

function viewFile() {
  if (localFiles.value.length === 0) return;

  // If multiple files, open the modal instead of opening a file directly
  if (localFiles.value.length > 1) {
    emit('open-modal');
    return;
  }

  // Single file: open directly in a new tab
  window.open(localFiles.value[0].fileUrl, '_blank');

  // Return focus to the parent cell for keyboard navigation
  // Use setTimeout to ensure the click event completes before focus changes
  setTimeout(() => {
    const button = document.activeElement as HTMLElement;
    const parentCell = button?.closest('[data-col-identifier]') as HTMLElement;

    if (parentCell) {
      parentCell.focus();
    }
  }, 0);
}

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>

<style scoped lang="scss">
.drag-area {
  @apply tw-border tw-border-gray-medium tw-rounded tw-p-2 tw-flex tw-flex-col tw-bg-white tw-relative;

  &:focus {
    border-color: var(--q-primary, var(--primary-color)) !important;
    outline: none;
  }
}

.drag-area--clickable {
  @apply tw-items-center tw-justify-center tw-border-dashed;
  &:hover {
    @apply tw-border-gray-dark tw-cursor-pointer;
  }
}

.drag-area--dragging {
  @apply tw-outline-dashed tw-outline-primary tw-outline-2 tw-border-transparent;
}

.drag-area--readonly {
  @apply tw-border-dashed;
}

.drag-area__placeholder {
  @apply tw-text-gray-medium-dark tw-text-xs;
}

.custom-field-file-input {
  display: none;
}
</style>
